import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { FiExternalLink, FiGithub, FiPackage } from 'react-icons/fi'
import './Projects.css'

const Projects = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const projects = [
    {
      title: 'TookLook',
      description: 'Comprehensive appointment scheduling platform connecting clients with service providers. Features real-time booking, map-based salon discovery, team management, QR code check-ins, payment integration, reviews & ratings, and analytics dashboard. Built with React Native, Socket.io for real-time updates, Firebase, Google Maps, and Redux Toolkit.',
      tags: ['React Native', 'Socket.io', 'Firebase', 'Maps', 'TypeScript', 'Redux'],
      android: 'https://play.google.com/store/apps/details?id=me.tooklook',
      ios: 'https://apps.apple.com/am/app/tooklook/id6473556907',
      color: '#6366f1',
      screenshot: '/images/tooklook.jpeg'
    },
    {
      title: 'Mapllo',
      description: 'Social dating and meetups platform with map-based discovery, real-time chat via WebSocket, event creation, user profiles, and location sharing. Features include feed with events and people discovery, favorites system, poster/event management, camera integration, and push notifications. Built with React Native, Expo, Google Maps with clustering, Firebase, Redux Toolkit, and real-time location tracking.',
      tags: ['React Native', 'WebSocket', 'Maps', 'Firebase', 'Expo', 'Redux'],
      android: 'https://play.google.com/store/apps/details?id=com.production.mapllo',
      ios: 'https://apps.apple.com/us/app/mapllo-dating-meetups/id1579691867',
      color: '#ec4899',
      screenshot: '/images/mapllo.jpeg'
    },
    {
      title: 'Onex Armenia',
      description: 'Full-featured express delivery platform with order tracking, Buy For Me service, warehouse management, home & locker delivery, export calculator, payment integration, and real-time notifications. Includes map-based tracking with clustering, multi-language support, and comprehensive order management system. Built with React Native, Google Maps, Firebase, Redux Toolkit, and TypeScript.',
      tags: ['React Native', 'Google Maps', 'Firebase', 'TypeScript', 'Redux', 'E-commerce'],
      android: 'https://play.google.com/store/apps/details?id=com.onex',
      ios: 'https://apps.apple.com/us/app/onex-online-express/id1182969336',
      color: '#8b5cf6',
      screenshot: '/images/onex_am.jpeg'
    },
    {
      title: 'Onex Georgia',
      description: 'Express delivery service platform for Georgia with order tracking, Buy For Me functionality, locker delivery system, map-based tracking, payment processing, and real-time order status updates. Features warehouse management, export services, and comprehensive order history. Built with React Native, Google Maps clustering, Firebase, Redux, and TypeScript.',
      tags: ['React Native', 'Google Maps', 'Firebase', 'TypeScript', 'Redux', 'Delivery'],
      android: 'https://play.google.com/store/apps/details?id=onex.geo',
      ios: 'https://apps.apple.com/us/app/onex-georgia/id6446616002',
      color: '#06b6d4',
      screenshot: '/images/onex_ge.jpeg'
    },
    {
      title: 'Muna Astrology',
      description: 'Comprehensive astrology app featuring AI-powered astrologer chat, daily horoscopes, tarot card readings, natal charts, and compatibility matching. Built with React Native, includes real-time messaging, in-app purchases, push notifications, and multi-language support (5 languages). Integrated Firebase, RevenueCat, Google Ads, and advanced analytics.',
      tags: ['React Native', 'AI Chat', 'Firebase', 'TypeScript', 'Redux', 'RevenueCat'],
      android: 'https://play.google.com/store/apps/details?id=com.horolite.horoscope',
      ios: 'https://apps.apple.com/us/app/muna-astrology-horoscope/id6474508082',
      color: '#f59e0b',
      screenshot: '/images/muna.jpeg'
    },
    {
      title: 'AiFit',
      description: 'AI-powered calorie counter and nutrition tracking app with camera-based food scanning, voice input, and real-time chat with nutritionists. Features include meal planning, recipe suggestions, activity tracking, water intake monitoring, and detailed statistics with charts. Built with React Native, TypeScript, Firebase, Vision Camera, and RevenueCat for subscriptions.',
      tags: ['React Native', 'AI', 'Camera', 'Firebase', 'TypeScript', 'Redux'],
      android: 'https://play.google.com/store/apps/details?id=com.aifit.caloriecounter',
      ios: 'https://apps.apple.com/au/app/aifit-easy-calorie-counter/id6596755374',
      color: '#10b981',
      screenshot: '/images/aifit.jpeg'
    },
    {
      title: 'Minecraft Content App',
      description: 'Minecraft content marketplace app for browsing and downloading maps (.mcworld) and resource packs (.mcaddon). Features include file download management with storage permissions, premium subscriptions via RevenueCat, Google Mobile Ads integration, Firebase Remote Config, and offline content access. Built with React Native, TypeScript, Redux Toolkit, and file system management.',
      tags: ['React Native', 'TypeScript', 'RevenueCat', 'Firebase', 'Redux', 'File System'],
      color: '#22c55e',
      isNpmPackage: false,
      isMinecraftApp: true,
      versions: [
        { name: 'Mine Scary Packs PE', version: 'v29', icon: '/images/minecraft_v29.webp' },
        { name: 'Scary Addons Mine Mania Mods', version: 'v28', icon: '/images/minecraft_v28.webp' },
        { name: 'Horror Block Packs & Maps', version: 'v27', icon: '/images/minecraft_v27.webp' },
        { name: 'Packs Worlds: Mods Explorer PE', version: 'v26', icon: '/images/minecraft_v26.webp' },
        { name: 'Worlds & PackJam PE', version: 'v24', icon: '/images/minecraft_v24.webp' },
        { name: 'Diabolical Heropack', version: 'v20', icon: '/images/minecraft_v20.webp' },
        { name: 'Horror Demon Hunters', version: 'v18', icon: '/images/minecraft_v18.webp' },
        { name: 'Scary Horror 99 Nights', version: 'v17', icon: '/images/minecraft_v17.webp' },
      ]
    },
    {
      title: 'rn-global-structure',
      description: 'Open-source React Native npm package providing a standardized project structure and boilerplate for building scalable mobile applications. Simplifies project setup with pre-configured folder organization, common utilities, and best practices. Helps developers maintain consistent architecture across React Native projects.',
      tags: ['React Native', 'NPM', 'Open Source', 'Boilerplate', 'TypeScript'],
      npm: 'https://www.npmjs.com/package/rn-global-structure',
      color: '#ef4444',
      isNpmPackage: true
    },
    {
      title: 'react-native-analytics-tools',
      description: 'Universal analytics module for React Native apps integrating Firebase Analytics, Facebook SDK, Branch.io, AppsFlyer, and Adjust. Provides a unified API for tracking events, user properties, and attribution across multiple analytics platforms. Simplifies multi-platform analytics implementation with a single interface.',
      tags: ['React Native', 'NPM', 'Analytics', 'Firebase', 'Open Source'],
      npm: 'https://www.npmjs.com/package/react-native-analytics-tools',
      color: '#3b82f6',
      isNpmPackage: true
    },
  ]

  return (
    <section id="projects" className="projects" ref={ref}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <h2 className="section-title">
            My <span className="gradient-text">Projects</span>
          </h2>
          <p className="section-subtitle">
            A collection of apps I've built and published
          </p>
        </motion.div>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="project-card"
              whileHover={{ y: -10 }}
            >
              <div className="project-content-wrapper">
                {!project.isNpmPackage ? (
                  <div className="project-mockup-wrapper">
                    <div className="phone-mockup" style={{ '--project-color': project.color }}>
                      <div className="phone-frame">
                        <div className="phone-notch"></div>
                        <div className="phone-screen">
                          <div className="phone-content">
                            {project.screenshot ? (
                              <img
                                src={project.screenshot}
                                alt={project.title}
                                className="phone-screenshot"
                              />
                            ) : project.isMinecraftApp ? (
                              <div className="minecraft-versions-grid">
                                <div className="minecraft-title">Minecraft Apps</div>
                                {project.versions?.map((version, idx) => (
                                  <div key={idx} className="minecraft-version-item">
                                    <div className="minecraft-app-icon-wrapper">
                                      <img
                                        src={version.icon}
                                        alt={version.name}
                                        className="minecraft-app-icon"
                                      />
                                    </div>
                                    <span className="minecraft-version-name">{version.name}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="phone-placeholder" style={{ background: `linear-gradient(135deg, ${project.color}15, ${project.color}05)` }}>
                                <div className="phone-placeholder-content">
                                  <div className="phone-placeholder-icon" style={{ background: project.color }}>
                                    {project.title.charAt(0)}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="project-mockup-wrapper">
                    <div className="npm-package-icon" style={{ '--project-color': project.color }}>
                      <FiPackage />
                    </div>
                  </div>
                )}
                <div className="project-info">
                  <div
                    className="project-header"
                    style={{ '--project-color': project.color }}
                  >
                    <h3>{project.title}</h3>
                  </div>
                  <div className="project-body">
                    <p>{project.description}</p>
                    <div className="project-tags">
                      {project.tags.map((tag) => (
                        <span key={tag} className="tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="project-footer">
                    {project.npm && (
                      <a
                        href={project.npm}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                      >
                        <FiPackage /> NPM Package
                      </a>
                    )}
                    {project.android && (
                      <a
                        href={project.android}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                      >
                        <FiExternalLink /> Android
                      </a>
                    )}
                    {project.ios && (
                      <a
                        href={project.ios}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link"
                      >
                        <FiExternalLink /> iOS
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Projects

